import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Users, Lightbulb, CheckCircle, TrendingUp, Heart, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const solutions = [
    {
      icon: BookOpen,
      title: "Educational Resources",
      description: "Quality tracing worksheets and workbooks designed for all learning types, including autism, SPED, ESL, and diverse learners."
    },
    {
      icon: Heart,
      title: "Inclusive Learning",
      description: "Solutions that celebrate neurodiversity and support every child's unique learning journey with specialized materials."
    },
    {
      icon: Target,
      title: "Research-Driven",
      description: "Evidence-based approaches informed by current best practices in education and special needs instruction."
    }
  ];

  const visionPhases = [
    {
      phase: "Phase 1",
      title: "Current Focus",
      status: "Active",
      description: "Delivering quality educational resources and tracing worksheets for diverse learners through TPT and Amazon.",
      icon: CheckCircle,
      color: "text-accent"
    },
    {
      phase: "Phase 2",
      title: "Expanding Solutions",
      status: "Coming Soon",
      description: "Developing partnerships with autism researchers, educators, and specialists to create advanced learning tools.",
      icon: TrendingUp,
      color: "text-primary"
    },
    {
      phase: "Phase 3",
      title: "Connected Platform",
      status: "Future Vision",
      description: "Building a network connecting educators, families, and solution providers in autism and special education.",
      icon: Lightbulb,
      color: "text-muted-foreground"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 sm:py-28 bg-gradient-to-br from-blue-50 via-white to-amber-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center fade-in">
            <div className="inline-flex items-center space-x-2 rounded-full bg-primary/10 px-4 py-2 mb-6">
              <span className="text-sm font-semibold text-primary">Solutions Provider</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-foreground">
              Next-Generation Solutions for <span className="gradient-text">Autism, SPED, and Diverse Learners</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
              All NextGen Networking Solutions is dedicated to advancing education through innovative resources, research partnerships, and a vision for connected learning communities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-lg px-8 py-6">
                <Link to="/products">
                  Explore Our Products
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-lg px-8 py-6 border-2">
                <Link to="/about">
                  Our Story
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Current Solutions Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
              Our <span className="gradient-text">Current Solutions</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We specialize in creating accessible, high-quality educational materials for all learners.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <Card key={index} className="border-2 hover:border-primary/50 transition-all card-hover">
                  <CardContent className="pt-8 pb-6 px-6">
                    <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                      <Icon className="h-7 w-7 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3 text-foreground">{solution.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{solution.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vision & Roadmap Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
              Our <span className="gradient-text">Vision & Roadmap</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              From quality resources today to a connected ecosystem of autism research and educational solutions tomorrow.
            </p>
          </div>
          <div className="max-w-5xl mx-auto space-y-6">
            {visionPhases.map((phase, index) => {
              const Icon = phase.icon;
              return (
                <Card key={index} className="border-2 hover:border-primary/30 transition-all">
                  <CardContent className="p-8">
                    <div className="flex flex-col md:flex-row md:items-start gap-6">
                      <div className={`flex-shrink-0 w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center ${phase.color}`}>
                        <Icon className="h-8 w-8" />
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
                          <div>
                            <h3 className="text-2xl font-bold text-foreground">{phase.title}</h3>
                            <p className="text-sm text-muted-foreground">{phase.phase}</p>
                          </div>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-2 sm:mt-0 ${
                            phase.status === 'Active' ? 'bg-accent/10 text-accent' : 
                            phase.status === 'Coming Soon' ? 'bg-primary/10 text-primary' : 
                            'bg-secondary text-muted-foreground'
                          }`}>
                            {phase.status}
                          </span>
                        </div>
                        <p className="text-muted-foreground leading-relaxed text-lg">
                          {phase.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                Why <span className="gradient-text">Choose Us</span>
              </h2>
              <p className="text-lg text-muted-foreground">
                Born from passion and rebuilt with purpose, we're committed to making a difference.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Target className="h-6 w-6 text-accent" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">Purpose-Driven Mission</h3>
                  <p className="text-muted-foreground">Every product and initiative is designed with real impact in mind, supporting educators and families.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">Community Focused</h3>
                  <p className="text-muted-foreground">We listen to educators, parents, and specialists to create solutions that truly serve diverse learning needs.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Lightbulb className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">Innovation & Research</h3>
                  <p className="text-muted-foreground">Committed to staying at the forefront of autism research and evidence-based educational practices.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Heart className="h-6 w-6 text-accent" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">Authentic & Caring</h3>
                  <p className="text-muted-foreground">Built with genuine passion for helping every child reach their full potential, regardless of their learning style.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Ready to Make a Difference?
            </h2>
            <p className="text-xl mb-10 text-white/90">
              Explore our educational resources or get in touch to learn more about our vision for the future.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="secondary" className="text-lg px-8 py-6 bg-white text-primary hover:bg-white/90">
                <Link to="/products">
                  View Products
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-2 border-white text-white hover:bg-white/10">
                <Link to="/contact">
                  Contact Us
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
